/* eslint-disable indent */

import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';

import { IntellimixType } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiIntellimixDeviceBehaviorPlugin } from '@shure/device-ui/intellimix/data-access';
import { FactoryResetText } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsFactoryResetBehaviorPlugin } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import {
	LightsImage,
	LightsImageProvider
} from '@shure/shared/angular/features/device-lights-settings/feature-device-image-next';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Mxa901DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		LightsImageProvider,
		CoverageImageProvider
{
	public readonly models: DeviceModel[] = [DeviceModel.MXA901R];

	constructor(protected readonly transloco: TranslocoService) {}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		const pages = [
			{
				route: DeviceUiRoute.Intellimix,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.intellimix')
			},
			{
				route: DeviceUiRoute.Presets,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.presets')
			},
			{
				route: DeviceUiRoute.Settings,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.settings')
			}
		];

		return of(pages);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Intellimix;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.Supported;
	}

	public getLightsImage(model: DeviceModel): LightsImage {
		return {
			imageUrl: `device-mxa9xx-${model === DeviceModel.MXA920S ? 's' : 'r'}.svg`
		};
	}

	public getCoverageImage(): CoverageImage {
		return {
			live: 'device-mxa901-r-back.svg',
			virtual: 'device-mxa901-r-back-line.svg',
			isImageRound: true
		};
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public revertAutomixerDefaultsCapability(_model: DeviceModel): boolean {
		return false;
	}

	public showPatentInfo(): boolean {
		return true;
	}

	public hideAgcMetering(_model: DeviceModel): boolean {
		return true;
	}

	public getIntellimixChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Agc, 1],
			[DspType.Aec, 2],
			[DspType.Nr, 3],
			[DspType.Peq, 4],
			[DspType.Compressor, 5],
			[DspType.Delay, 6]
		]);
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}
}
